import { useState, useContext, useEffect } from 'react';
import { ApiContext } from '../../App';

const AddPartnerForm = ({ onClose, onSuccess }) => {
  const API = useContext(ApiContext);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city: '',
    internalManager: '',
    contractSigned: false,
    technologies: '',
    tags: '',
    status: 'cold',
    employeeCount: '',
    linkedinUrl: '',
  });

  // Bloquer le scroll du body quand la modal est ouverte
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/api/partners', {
        ...formData,
        technologies: formData.technologies ? formData.technologies.split(',').map(tech => tech.trim()) : [],
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : [],
        employeeCount: formData.employeeCount ? parseInt(formData.employeeCount) : null,
      });
      if (onSuccess) {
        await onSuccess(); // Rafraîchir la liste des entreprises
      }
      onClose(); // Fermer la modal après succès
    } catch (error) {
      console.error('Error adding partner:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Container de la modal */}
        <div
          className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header du formulaire */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="bi bi-building-fill text-white"></i>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white m-0">Nouvelle Entreprise</h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <i className="bi bi-x-lg text-xl"></i>
              </button>
            </div>
          </div>

          {/* Contenu scrollable */}
          <div className="overflow-y-auto flex-1 p-6">
            {/* Formulaire */}
            <form onSubmit={handleFormSubmit} className="space-y-4">
          {/* Nom et Description - Pleine largeur */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Saisir le nom de l'entreprise"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description 
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Saisir la description de l'entreprise"
            />
          </div>

          {/* Grille 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Colonne 1 */}
            <div className="space-y-4">
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Ville
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Saisir la ville"
                />
              </div>

              {/* Internal Manager */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Manager Interne
                </label>
                <input
                  type="text"
                  name="internalManager"
                  value={formData.internalManager}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Saisir le nom du manager interne"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="cold">Froid</option>
                  <option value="prospect">Prospect</option>
                  <option value="client">Client</option>
                  <option value="old client">Ancien Client</option>
                </select>
              </div>

              {/* Employee Count */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nombre d'employés
                </label>
                <input
                  type="number"
                  name="employeeCount"
                  value={formData.employeeCount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="50"
                  min="1"
                />
              </div>
            </div>

            {/* Colonne 2 */}
            <div className="space-y-4">
              {/* Technologies */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Technologies
                </label>
                <input
                  type="text"
                  name="technologies"
                  value={formData.technologies}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="React, Node.js, Python..."
                />
                <p className="mt-1 text-xs text-slate-500">Séparées par des virgules</p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="startup, fintech, remote..."
                />
                <p className="mt-1 text-xs text-slate-500">Séparés par des virgules</p>
              </div>

              {/* LinkedIn URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  URL LinkedIn
                </label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="https://www.linkedin.com/company/..."
                />
              </div>

              {/* Contract Signed */}
              <div className="flex items-center pt-7">
                <input
                  type="checkbox"
                  name="contractSigned"
                  checked={formData.contractSigned}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300 rounded"
                />
                <label className="ml-2 block text-sm text-slate-700">
                  Contrat Signé
                </label>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4 mt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
            >
              Ajouter l'entreprise
            </button>
          </div>
        </form>
      </div>
        </div>
      </div>
    </>
  );
};

export default AddPartnerForm;